import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PublishIcon from '@mui/icons-material/Publish';

//import PlanningWorkbenchShell from '../bieprofile/PlanningWorkbenchShell';
import {
  FWizard,
  FWizardChecklist,
  FWizardStepConfig,
  FWizardSummaryCard,
} from '../wizard';
//import { PageProps } from '../../../components/types/coretypes';

import BaselineCoverageGrid from './BaselineCoverageGrid';
import BaselineIntegrityCards from './BaselineIntegrityCards';
import ForecastQualityPanel from './ForecastQualityPanel';
import ForecastVariableGrid from './ForecastVariableGrid';
import ForecastResultsGrid from './ForecastResultsGrid';
import ForecastQualityIssueGrid from './ForecastQualityIssueGrid';
import {
  fetchForecastingWizard,
  fetchForecastResults,
  fetchForecastQualityIssues,
  publishForecastBaseline,
  runMissingPeriodForecast,
  saveForecastVariables,
  validateBaselineIntegrity,
  validateForecastQuality,
} from './bieforecasting.apis';
import {
  ForecastingStageState,
  ForecastingStepCode,
  IForecastResultRow,
  IForecastingWorkbench,
  IForecastQualityIssue,
  IForecastVariableRow,
} from './bieforecasting.models';

import { PlanningPageProps, PlanningWorkbenchShell } from '../planningcore';
import FPlanningJourney from '../core/FPlanningJourney';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';

type State = {
  activeStep: ForecastingStepCode;
  loading: boolean;
  error: string;
  success: string;
  warning: string;
  phases: any[];
  workbench: IForecastingWorkbench | null;
  notes: string;
  variableRows: IForecastVariableRow[];
  forecastRows: IForecastResultRow[];
  forecastRowCount: number;
  forecastPage: number;
  forecastPageSize: number;
  loadingResults: boolean;
  qualityIssues: IForecastQualityIssue[];
  qualityIssueCount: number;
  qualityIssuePage: number;
  qualityIssuePageSize: number;
  loadingQualityIssues: boolean;
};



type ForecastBaselineWizardProps = PlanningPageProps & {
  batchid: number;
  onClose?: () => void;
  onChanged?: () => void | Promise<void>;
};

class ForecastBaselineWizard extends React.Component<ForecastBaselineWizardProps, State> {
  state: State = {
    activeStep: 'setup',
    loading: false,
    error: '',
    success: '',
    warning: '',
    phases: [],
    workbench: null,
    notes: '',
    variableRows: [],
    forecastRows: [],
    forecastRowCount: 0,
    forecastPage: 0,
    forecastPageSize: 25,
    loadingResults: false,
    qualityIssues: [],
    qualityIssueCount: 0,
    qualityIssuePage: 0,
    qualityIssuePageSize: 25,
    loadingQualityIssues: false,
  };

  componentDidMount() {
    this.initialize();
  }

  // private getPageParam = (key: string): unknown => {
  //   const params: any = this.props.params ?? {};
  //   return params[key] ?? params?.get?.(key) ?? undefined;
  // };

  // private getPlanningCycleId = (): number => {
  //   const value = this.getPageParam('planningcycleid');
  //   const result = Number(value);
  //   return Number.isFinite(result) && result > 0 ? result : 0;
  // };

  private getPlanningCycleId = (): number => {
    // const params = (this.props.params || {}) as any;
    // const value =
    //   params.planningcycleid ??
    //   params.pci ??
    //   params.planningCycleId;
    const value = this.props.plancontext?.pci;
    return Number(value || 0);
  };

  private initialize = async () => {
    const planningcycleid = this.getPlanningCycleId();

    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is missing. Please set your preferred context in the Planning Setup ',
      });
      return;
    }

    this.setState({ loading: true, error: '', success: '' });

    try {
      const [phases] = await Promise.all([fetchPlanningJourneyPhases()]);
      this.setState({ phases });
      await this.getWorkbench();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private getWorkbench = async (preserveActiveStep = false) => {
    const planningcycleid = this.getPlanningCycleId();
    console.log('bieforecast.pci:', planningcycleid);
    if (!planningcycleid) {
      this.setState({
        error: 'Planning Cycle context is required. Return to Planning Cycle and reopen BIE Forecasting.',
      });
      return;
    }

    this.setState({ loading: true, error: '', warning: '' });
    try {
      const workbench = await fetchForecastingWizard(planningcycleid, this.props.batchid);
      const serverStep = (workbench.currentstep || 'setup') as ForecastingStepCode;
      const activeStep =
        preserveActiveStep && this.state.activeStep
          ? this.state.activeStep
          : serverStep;

      this.setState({
        workbench,
        activeStep,
        variableRows: workbench.variables || [],
      });

      if (workbench.execution?.totalrows) {
        await this.loadForecastResults(0, this.state.forecastPageSize);
      } else {
        this.setState({ forecastRows: [], forecastRowCount: 0 });
      }

      if (workbench.quality?.validationix) {
        await this.loadQualityIssues(0, this.state.qualityIssuePageSize);
      } else {
        this.setState({ qualityIssues: [], qualityIssueCount: 0 });
      }
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };

  private mutate = async (
    action: () => Promise<unknown>,
    success: string,
  ) => {
    this.setState({ loading: true, error: '', success: '', warning: '' });
    try {
      await action();
      this.setState({ success });

      /*
       * A panel action completes the current business milestone.
       * It must not navigate the wizard. The refreshed workbench returns
       * the new COMPLETE/LOCKED states, which enable the wizard CONTINUE
       * action. The planner then advances explicitly.
       */
      await this.getWorkbench(true);
      await this.props.onChanged?.();
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loading: false });
    }
  };


  private loadForecastResults = async (
    page = this.state.forecastPage,
    pageSize = this.state.forecastPageSize,
  ) => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) return;
    this.setState({ loadingResults: true });
    try {
      const result = await fetchForecastResults({ planningcycleid, batchid: this.props.batchid, page, pageSize });
      this.setState({
        forecastRows: result.rows,
        forecastRowCount: result.rowCount,
        forecastPage: result.page,
        forecastPageSize: result.pageSize,
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loadingResults: false });
    }
  };

  private loadQualityIssues = async (
    page = this.state.qualityIssuePage,
    pageSize = this.state.qualityIssuePageSize,
  ) => {
    const planningcycleid = this.getPlanningCycleId();
    if (!planningcycleid) return;
    this.setState({ loadingQualityIssues: true });
    try {
      const result = await fetchForecastQualityIssues({ planningcycleid, batchid: this.props.batchid, page, pageSize });
      this.setState({
        qualityIssues: result.rows,
        qualityIssueCount: result.rowCount,
        qualityIssuePage: result.page,
        qualityIssuePageSize: result.pageSize,
      });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    } finally {
      this.setState({ loadingQualityIssues: false });
    }
  };

  private saveVariables = async (rows: IForecastVariableRow[]) => {
    if (!rows.length) return;
    const planningcycleid = this.getPlanningCycleId();
    await this.mutate(
      () => saveForecastVariables(this.props.batchid, rows),
      'Forecast variables confirmed. Forecast and quality stages were invalidated where required.',
    );
  };

  private validateBaseline = async () => {
    const planningcycleid = this.getPlanningCycleId();
    await this.mutate(
      () => validateBaselineIntegrity(this.props.batchid),
      'Historical baseline integrity validated. Continue when ready.',
    );
  };

  private runForecast = async () => {
    const planningcycleid = this.getPlanningCycleId();
    await this.mutate(
      () => runMissingPeriodForecast(this.props.batchid, {}),
      'Forecast generation completed. Continue when ready.',
    );
  };

  private validateQuality = async () => {
    const planningcycleid = this.getPlanningCycleId();
    await this.mutate(
      () => validateForecastQuality(this.props.batchid),
      'Forecast quality validated. Continue when ready.',
    );
  };

  private publish = async () => {
    const planningcycleid = this.getPlanningCycleId();
    await this.mutate(
      () => publishForecastBaseline(this.props.batchid, this.state.notes),
      'Forecast baseline published.',
    );
  };

  private stageState = (code: ForecastingStepCode): ForecastingStageState =>
    this.state.workbench?.stages?.[code]?.state || 'LOCKED';

  private canEnter = (code: ForecastingStepCode): boolean =>
    this.stageState(code) !== 'LOCKED';

  private stepStatus = (code: ForecastingStepCode): any => {
    const state = this.stageState(code);
    if (state === 'COMPLETE') return 'complete';
    if (state === 'BLOCKED') return 'error';
    if (state === 'STALE') return 'warning';
    if (state === 'IN_PROGRESS') return 'active';
    return 'pending';
  };

  private renderContext = () => {
    const context = this.state.workbench?.context;
    if (!context) return null;

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Planning Cycle</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {context.planningcyclename || context.planningcyclecode}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Forecast Batch</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {this.state.workbench?.batch?.batchname || this.state.workbench?.batch?.batchcode || '-'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Fiscal Year</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{context.fiscalyear}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography variant="caption" color="text.secondary">Historical Dataset</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {context.historicaldatasetname || context.historicaldatasetcode || '-'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography variant="caption" color="text.secondary">Readiness</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={`${this.state.workbench?.readiness.score || 0}%`}
                color={(this.state.workbench?.readiness.score || 0) >= 90 ? 'success' : 'warning'}
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  private renderSetup = () => {
    const wb = this.state.workbench;
    const context = wb?.context;
    const setupComplete = wb?.stages.setup.state === 'COMPLETE';
    const blockingVariables = wb?.stages.integrity.blockingcount || 0;
    const warningVariables = wb?.stages.integrity.warningcount || 0;
    const coverageFrom = context?.expectedhistoricalstartdate || context?.observedcoveragestartdate || '';
    const coverageTo = context?.expectedhistoricalenddate || context?.observedcoverageenddate || '';

    return (
      <Stack spacing={2}>
        <Alert severity={setupComplete ? 'success' : 'info'}>
          {wb?.stages.setup.requirement || 'Forecast Setup is evaluated from the Planning Cycle and Historical Dataset.'}
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField size="small" label="Batch Name" value={wb?.batch?.batchname || ''} fullWidth disabled />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField size="small" label="Fiscal Year" value={context?.fiscalyear || ''} fullWidth disabled />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              size="small"
              label="Historical Dataset"
              value={context?.historicaldatasetname || context?.historicaldatasetcode || ''}
              fullWidth
              disabled
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField size="small" label="Coverage From" value={coverageFrom} fullWidth disabled />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField size="small" label="Coverage To" value={coverageTo} fullWidth disabled />
          </Grid>
        </Grid>

        {setupComplete && blockingVariables > 0 && (
          <Alert severity="warning">
            Forecast Setup is complete. Historical Integrity still has {blockingVariables} blocking variable(s) and {warningVariables} warning variable(s).
          </Alert>
        )}
      </Stack>
    );
  };

  private renderIntegrity = () => {
    const wb = this.state.workbench;
    return (
      <Stack spacing={2}>
        <BaselineIntegrityCards rows={wb?.coverage || []} />
        <FWizardChecklist
          title="Integrity Gate"
          subtitle="Forecasting can continue only when certified historical coverage satisfies the server-side gate."
          items={[
            { code: 'facts', label: 'Approved historical facts are available', checked: (wb?.coverage.length || 0) > 0, severity: 'warning' },
            { code: 'continuity', label: 'Required historical coverage is continuous', checked: (wb?.stages.integrity.blockingcount || 0) === 0, severity: 'error' },
            { code: 'variables', label: 'Forecast variables are configured', checked: (wb?.variables.length || 0) > 0, severity: 'warning' },
          ]}
        />
        <BaselineCoverageGrid rows={wb?.coverage || []} loading={this.state.loading} />
        <Button
          variant="contained"
          onClick={this.validateBaseline}
          disabled={!wb?.actions.canValidateBaseline || this.state.loading}
        >
          Validate Baseline Integrity
        </Button>
      </Stack>
    );
  };

  private renderVariables = () => (
    <Stack spacing={2}>
      <Alert severity="info">
        Review and confirm the Forecast Variable configuration. Any saved change invalidates downstream forecast and quality results until regeneration.
      </Alert>
      <ForecastVariableGrid
        rows={this.state.variableRows}
        loading={this.state.loading}
        editable={Boolean(this.state.workbench?.actions.canConfigureVariables)}
        onRowsChange={(variableRows) => this.setState({ variableRows })}
        onSave={this.saveVariables}
      />
    </Stack>
  );

  private renderForecast = () => {
    const wb = this.state.workbench;
    return (
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <FWizardSummaryCard label="Batch" value={wb?.batch?.batchcode || 'Not created'} tone={wb?.batch ? 'success' : 'warning'} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FWizardSummaryCard label="Forecast Start" value={wb?.batch?.forecaststartdate || '-'} tone="info" />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FWizardSummaryCard label="Readiness" value={`${wb?.readiness.score || 0}%`} tone={(wb?.readiness.score || 0) >= 90 ? 'success' : 'warning'} />
          </Grid>
        </Grid>
        <Alert severity="info">
          Only missing periods for forecastable variables are generated. Fixed assumptions and approved facts remain unchanged.
        </Alert>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={this.runForecast}
          disabled={!wb?.actions.canGenerate || this.state.loading}
        >
          {wb?.execution?.stale ? 'Regenerate Forecast' : 'Generate Forecast for Missing Periods'}
        </Button>
        {wb?.execution && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FWizardSummaryCard label="Generated Rows" value={String(wb.execution.generatedrows)} tone="info" />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FWizardSummaryCard label="Preserved Facts" value={String(wb.execution.preservedfacts)} tone="success" />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FWizardSummaryCard label="Warnings" value={String(wb.execution.warningcount)} tone={wb.execution.warningcount ? 'warning' : 'success'} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FWizardSummaryCard label="Errors" value={String(wb.execution.errorcount)} tone={wb.execution.errorcount ? 'error' : 'success'} />
            </Grid>
          </Grid>
        )}
        <ForecastResultsGrid
          rows={this.state.forecastRows}
          rowCount={this.state.forecastRowCount}
          loading={this.state.loadingResults}
          paginationModel={{ page: this.state.forecastPage, pageSize: this.state.forecastPageSize }}
          onPaginationModelChange={(model) => this.loadForecastResults(model.page, model.pageSize)}
          onRefresh={() => this.loadForecastResults()}
        />
      </Stack>
    );
  };

  private renderQuality = () => {
    const wb = this.state.workbench;
    return (
      <Stack spacing={2}>
        {wb?.quality?.stale && (
          <Alert severity="warning">
            The previous quality validation is stale because forecast inputs or generated rows changed. Revalidate before publication.
          </Alert>
        )}
        <ForecastQualityPanel quality={wb?.quality} />
        <Button
          variant="contained"
          onClick={this.validateQuality}
          disabled={!wb?.actions.canValidateQuality || this.state.loading}
        >
          {wb?.quality?.validationix ? 'Revalidate Forecast Quality' : 'Validate Forecast Quality'}
        </Button>
        <ForecastQualityIssueGrid
          rows={this.state.qualityIssues}
          rowCount={this.state.qualityIssueCount}
          loading={this.state.loadingQualityIssues}
          paginationModel={{ page: this.state.qualityIssuePage, pageSize: this.state.qualityIssuePageSize }}
          onPaginationModelChange={(model) => this.loadQualityIssues(model.page, model.pageSize)}
          onRefresh={() => this.loadQualityIssues()}
        />
      </Stack>
    );
  };

  private renderPublish = () => {
    const wb = this.state.workbench;
    const published = Boolean(wb?.publication?.published);
    const execution = wb?.execution;
    const quality = wb?.quality;

    return (
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Publishing certifies and freezes this governed forecast baseline. The published version becomes available to BIE Simulation.
        </Typography>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5 }}>Publication Summary</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Forecast Batch</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {wb?.batch?.batchname || wb?.batch?.batchcode || '-'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">Quality Status</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{quality?.status || '-'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">Confidence</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{`${quality?.confidence ?? 0}%`}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">Model Outputs</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{execution?.generatedrows ?? 0}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="text.secondary">Exceptions</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{quality?.exceptions ?? 0}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <TextField
          label="Publication Notes"
          value={published ? (wb?.publication?.notes || this.state.notes) : this.state.notes}
          onChange={(event) => this.setState({ notes: event.target.value })}
          multiline
          minRows={3}
          fullWidth
          disabled={published}
          helperText={published ? 'Publication notes are frozen with the certified baseline.' : 'Optionally record the planning context, assumptions or approval basis supporting publication.'}
        />

        {!published && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PublishIcon />}
            onClick={this.publish}
            disabled={!wb?.actions.canPublish || this.state.loading}
          >
            Publish Forecast Baseline
          </Button>
        )}

        {published && (
          <Alert severity="success">
            Forecast baseline published{wb?.publication?.publishedat ? ` on ${wb.publication.publishedat}` : ''}. The certified version is ready for BIE Simulation. Return to Forecast Batches to complete the Forecasting phase.
          </Alert>
        )}
      </Stack>
    );
  };

  private steps = (): FWizardStepConfig[] => {
    const wb = this.state.workbench;
    const cfg = (code: ForecastingStepCode, title: string, subtitle: string, description: string, render: () => React.ReactNode): FWizardStepConfig => ({
      code,
      title,
      subtitle,
      description,
      status: this.stepStatus(code),
      canEnter: this.canEnter(code),
      canContinue: code === 'publish'
        ? Boolean(wb?.publication?.published)
        : this.stageState(code) === 'COMPLETE',
      requirement: this.stageState(code) === 'COMPLETE' ? undefined : wb?.stages?.[code]?.requirement,
      render,
    });

    return [
      cfg('setup', 'Forecast Setup', 'Confirm the Planning Cycle and Historical Dataset.', 'Forecasting consumes approved historical coverage; Scenario and Load Session are not setup prerequisites.', this.renderSetup),
      cfg('integrity', 'Baseline Integrity', 'Validate certified historical coverage.', 'Forecasting cannot continue while required historical inputs are blocked.', this.renderIntegrity),
      cfg('variables', 'Forecast Variables', 'Review forecastable variables and methods.', 'Variable changes invalidate downstream results until regeneration.', this.renderVariables),
      cfg('forecast', 'Generate Forecast', 'Estimate only missing forecastable periods.', 'Approved facts and fixed assumptions are preserved.', this.renderForecast),
      cfg('quality', 'Quality Review', 'Validate confidence, exceptions and outliers.', 'Publication requires a current, non-stale quality result.', this.renderQuality),
      cfg('publish', 'Publish Baseline', 'Certify the forecast for BIE Simulation.', 'Publication freezes the governed forecast baseline and enables simulation.', this.renderPublish),
    ];
  };

  /** Exposed to the page registry or parent workbench when a global refresh is requested. */
  public refresh = async (): Promise<void> => {
    await this.getWorkbench();
  };

  render() {
    const wb = this.state.workbench;
    const exitAction = (
      <Button variant="outlined" onClick={this.props.onClose}>
        Back to Forecast Batches
      </Button>
    );

    return (
      <PlanningWorkbenchShell
        {...this.props}
        onBack={this.props.onClose || this.props.onBack}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            currentPhaseCode={wb?.context.phasecode || 'FORECASTING'}
          />
        }
      
        title="BIE Forecasting"
        subtitle="Validate, generate, quality-check and publish one governed Forecast Batch."
        icon={<AutoGraphIcon />}
        loading={this.state.loading}
        error={this.state.error}
        success={this.state.success}
        warning={this.state.warning}
        onRefresh={this.getWorkbench}
        actions={exitAction}
      >
        <Box>
          {wb ? (
            <FWizard
              title="Forecast Baseline Wizard"
              subtitle="Stages unlock only when their server-side prerequisites are satisfied."
              activeStep={this.state.activeStep}
              steps={this.steps()}
              loading={this.state.loading}
              error=""
              success=""
              warning=""
              onStepChange={(activeStep) => this.setState({ activeStep: activeStep as ForecastingStepCode })}
              onFinish={wb.publication?.published ? this.props.onClose : undefined}
              finishLabel={wb.publication?.published ? 'Back to Forecast Batches' : 'Publish'}
            />
          ) : (
            !this.state.loading && <Alert severity="info">Forecasting workbench context is not available.</Alert>
          )}
        </Box>
      </PlanningWorkbenchShell>
    );
  }
}

export default ForecastBaselineWizard;
